/* eslint-disable max-classes-per-file */
/* eslint-disable no-useless-constructor */
import { PetTypeEnum } from '@rover/rsdk/src/apiClient/latest';
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

export enum LemonadeEntrypointSourceEnum {
  PetSelector = 'pet-selector',
  PetProfile = 'pet-profile',
  PostBooking = 'post-booking',
  WhatsNext = 'whats-next',
}

abstract class LemonadePetInsuranceEvent extends AnalyticsEvent {
  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.service_offerings.insurance;
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on the Lemonade Entry point
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 *
 * &nbsp;
 */
export class LemonadeEntryLinkClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-entry-link-click';

  constructor(eventProperties: { flowSource: LemonadeEntrypointSourceEnum }) {
    super(eventProperties, { flowSource: eventProperties.flowSource });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when the Lemonade Entry point is viewed/loaded
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 *
 * &nbsp;
 */
export class LemonadeEntryLinkView extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-entry-link-view';

  constructor(eventProperties: { flowSource: LemonadeEntrypointSourceEnum }) {
    super(eventProperties, { flowSource: eventProperties.flowSource });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when the Pet + User Info Page is loaded
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoPageLoad extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-petuserinfo-page-load';

  constructor(eventProperties: {
    loggedIn: boolean;
    state?: string;
    flowSource?: 'email' | 'entry_point' | string | null;
  }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on add dog or add cat CTA
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoAddPetClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-petuserinfo-add-pet-click';

  constructor(eventProperties: { petType: PetTypeEnum }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on pet icon to edit a pet profile
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoEditPetClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-petuserinfo-edit-pet-click';

  constructor(eventProperties: { petOpk: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on View my price estimate CTA
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoLinkClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-petuserinfo-link-click';

  constructor(eventProperties: { petOpk: string }) {
    super(eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on Continue to Lemonade CTA
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoRedirectContinueLinkClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-redirect-continue-link-click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on Stay on Rover CTA
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoRedirectStayLinkClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-redirect-stay-link-click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on the Sign Up button on the Pet Insurance page
 * For more context, check the [FS](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP)
 * https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4637492096/FS+Rover+Lemonade+Insurance+Partnership+MVP
 *
 * &nbsp;
 */
export class LemonadePetUserInfoSignupClick extends LemonadePetInsuranceEvent {
  eventName = 'lemonade-petuserinfo-signup-click';
}
